import { useCallback, useEffect, useState } from 'react';
import { Button, Input, Modal } from '@cuito/ui';
import { readErrorMeta } from '@cuito/api-client';
import type { ProductDto, ProductStatus } from '@cuito/types';
import { catalogApi } from '../api/catalog';
import { PRICE_UNAVAILABLE_TEXT, formatServerYuan, yuanToCents } from '../lib/schemeForm';
import { useAuthStore } from '../stores/authStore';

/** 商品状态显示词（`05-ui-design-system.md` §7.3 与 `PRODUCT_STATUSES` 一一对应） */
const STATUS_LABELS: Record<ProductStatus, string> = {
  DRAFT: '草稿',
  ACTIVE: '上架',
  INACTIVE: '下架',
};

const PAGE_SIZE = 20;

interface ProductFormState {
  name: string;
  /** 表单里是「元」字符串，提交前一次性转成整数分 */
  price: string;
  category: string;
  description: string;
  imageUrl: string;
  previewVideoUrl: string;
  taobaoUrl: string;
  isAlbum: boolean;
}

const EMPTY_FORM: ProductFormState = {
  name: '',
  price: '',
  category: '',
  description: '',
  imageUrl: '',
  previewVideoUrl: '',
  taobaoUrl: '',
  isAlbum: false,
};

/** 商品 → 表单初值。`price` 是服务端下发的两位小数字符串，原样回填不打回数字 */
function toFormState(product: ProductDto): ProductFormState {
  return {
    name: product.name,
    price: product.price,
    category: product.category ?? '',
    description: product.description ?? '',
    imageUrl: product.imageUrl ?? '',
    previewVideoUrl: product.previewVideoUrl ?? '',
    taobaoUrl: product.taobaoUrl ?? '',
    isAlbum: product.isAlbum,
  };
}

/**
 * 表单 → 请求体。校验文案取自 `05-ui-design-system.md` §3.2。
 *
 * 可选字段为空时**不下发该键**：服务端的 `@IsUrl` 见到空串会判失败，
 * 而「没填淘宝链接」是合法状态。
 */
function buildBody(
  form: ProductFormState,
): { ok: true; body: Record<string, unknown> } | { ok: false; message: string } {
  const name = form.name.trim();
  if (name === '') return { ok: false, message: '请填写商品名称' };

  const priceCents = yuanToCents(form.price);
  if (priceCents === null || priceCents <= 0) return { ok: false, message: '价格必须大于 0' };

  const taobaoUrl = form.taobaoUrl.trim();
  if (taobaoUrl !== '' && !taobaoUrl.startsWith('https://')) {
    return { ok: false, message: '淘宝链接必须以 https:// 开头' };
  }

  const optional = (value: string): string | undefined => {
    const trimmed = value.trim();
    return trimmed === '' ? undefined : trimmed;
  };

  return {
    ok: true,
    body: {
      name,
      // 从整数分反推两位小数串，保证满足服务端 `^\d+(\.\d{1,2})?$`
      price: (priceCents / 100).toFixed(2),
      description: optional(form.description),
      category: optional(form.category),
      imageUrl: optional(form.imageUrl),
      previewVideoUrl: optional(form.previewVideoUrl),
      taobaoUrl: optional(taobaoUrl),
      isAlbum: form.isAlbum,
    },
  };
}

/**
 * 商品管理列表（`05-ui-design-system.md` §3.2 `/products`）。
 *
 * **这里全是单品**：促销方案不是商品、不出现在本列表，改由 `/promotion-schemes` 维护。
 * 加载/空/错三态与表格样式照 `ShopsPage.tsx`，只把「刷新」换成搜索与新建。
 */
export default function ProductsPage() {
  const canWrite = useAuthStore((s) => s.hasPermission('product:write'));

  const [products, setProducts] = useState<ProductDto[]>([]);
  const [total, setTotal] = useState(0);
  const [keywordInput, setKeywordInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<ProductDto | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<ProductFormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await catalogApi.listProducts({
        page,
        pageSize: PAGE_SIZE,
        ...(keyword === '' ? {} : { keyword }),
      });
      setProducts(res.items);
      setTotal(res.total);
    } catch (e) {
      // 403 是权限问题、其余归为网络/服务端问题：两者的下一步动作不同，文案必须分开
      setError(readErrorMeta(e)?.status === 403 ? '无权查看该社团的商品' : '加载失败，请重试');
    } finally {
      setLoading(false);
    }
  }, [keyword, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = (): void => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (product: ProductDto): void => {
    setEditing(product);
    setForm(toFormState(product));
    setFormError(null);
    setShowForm(true);
  };

  const submit = async (): Promise<void> => {
    const built = buildBody(form);
    if (!built.ok) {
      setFormError(built.message);
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      if (editing) {
        await catalogApi.updateProduct(editing.id, built.body);
      } else {
        await catalogApi.createProduct(built.body);
      }
      setShowForm(false);
      await load();
    } catch {
      setFormError(editing ? '保存商品失败，请重试' : '创建商品失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (product: ProductDto): Promise<void> => {
    const next: ProductStatus = product.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    try {
      await catalogApi.setProductStatus(product.id, next);
      await load();
    } catch {
      setError('操作失败，请重试');
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <h1 className="mb-6 text-2xl font-bold text-gray-900">商品管理</h1>

      <div className="mb-4 flex items-center gap-3">
        <form
          className="flex flex-1 items-center gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            setKeyword(keywordInput.trim());
          }}
        >
          <input
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            placeholder="搜索商品名称"
            className="h-12 w-full max-w-md rounded-lg border-2 border-black bg-white px-3 text-base outline-none focus:ring-2 focus:ring-black"
          />
          <Button type="submit" variant="secondary">
            搜索
          </Button>
        </form>
        {canWrite && <Button onClick={openCreate}>+ 新建商品</Button>}
      </div>

      {loading && <p className="py-8 text-center text-gray-500">加载中…</p>}
      {!loading && error && <p className="font-bold text-red-600">{error}</p>}

      {!loading && !error && products.length === 0 && (
        <p className="text-gray-500">暂无商品，点击「新建商品」创建</p>
      )}

      {!loading && !error && products.length > 0 && (
        <table className="w-full rounded-lg border-2 border-black">
          <thead className="bg-gray-50">
            <tr>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">名称</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">价格</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">分类</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">状态</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">淘宝链接</th>
              {canWrite && (
                <th className="border-b-2 border-black px-4 py-3 text-left font-bold">操作</th>
              )}
            </tr>
          </thead>
          <tbody>
            {products.map((product) => (
              <tr key={product.id}>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">{product.name}</td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">
                  {formatServerYuan(product.price) ?? PRICE_UNAVAILABLE_TEXT}
                </td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">
                  {product.category ?? '-'}
                </td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">
                  {STATUS_LABELS[product.status]}
                </td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">
                  {product.taobaoUrl ? (
                    <a
                      href={product.taobaoUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium text-blue-600 hover:text-blue-800"
                    >
                      淘宝链接
                    </a>
                  ) : (
                    '-'
                  )}
                </td>
                {canWrite && (
                  <td className="border-b border-gray-200 px-4 py-3 text-sm">
                    <div className="flex gap-3">
                      <button
                        onClick={() => openEdit(product)}
                        className="font-medium text-blue-600 hover:text-blue-800"
                      >
                        编辑
                      </button>
                      <button
                        onClick={() => void toggleStatus(product)}
                        className="font-medium text-gray-600 hover:text-gray-900"
                      >
                        {product.status === 'ACTIVE' ? '下架' : '上架'}
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!loading && !error && totalPages > 1 && (
        <div className="mt-4 flex items-center gap-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            上一页
          </Button>
          <span className="text-sm text-gray-600">
            第 {page} / {totalPages} 页
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >
            下一页
          </Button>
        </div>
      )}

      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title={editing ? '编辑商品' : '新建商品'}
      >
        <div className="flex flex-col gap-3">
          {formError && (
            <p className="rounded-lg border-2 border-red-500 bg-red-50 p-2 text-sm text-red-600">
              {formError}
            </p>
          )}

          <Input
            label="商品名称"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <Input
            label="价格（元）"
            value={form.price}
            inputMode="decimal"
            onChange={(e) => setForm({ ...form, price: e.target.value })}
          />
          <Input
            label="分类"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
          />

          <label className="flex flex-col gap-1">
            <span className="text-sm font-bold">描述</span>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="min-h-[88px] rounded-lg border-2 border-black bg-white px-3 py-2 text-base outline-none focus:ring-2 focus:ring-black"
            />
          </label>

          <Input
            label="图片 URL"
            value={form.imageUrl}
            onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
          />
          <Input
            label="预览视频 URL"
            value={form.previewVideoUrl}
            onChange={(e) => setForm({ ...form, previewVideoUrl: e.target.value })}
          />
          <Input
            label="淘宝链接"
            placeholder="https://"
            value={form.taobaoUrl}
            onChange={(e) => setForm({ ...form, taobaoUrl: e.target.value })}
          />

          <label className="flex min-h-[44px] items-center gap-3 text-sm font-bold">
            <input
              type="checkbox"
              className="h-5 w-5"
              checked={form.isAlbum}
              onChange={(e) => setForm({ ...form, isAlbum: e.target.checked })}
            />
            专辑
          </label>

          <div className="mt-2 flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setShowForm(false)} disabled={saving}>
              取消
            </Button>
            <Button onClick={() => void submit()} disabled={saving} className="disabled:opacity-50">
              {saving ? (editing ? '保存中…' : '创建中…') : editing ? '保存' : '创建商品'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
